import React, { createContext, useContext, useState, useEffect } from 'react';
import { loadTheme } from './themeLoader';
import { THEME } from '@cs-cms/types';

export type AppTheme = 'light' | 'dark';

interface ThemeContextType {
  theme: AppTheme;
  toggleTheme: () => void;
  isLoading: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const getOSThemePreference = (): AppTheme => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? THEME.DARK : THEME.LIGHT;
  }
  return THEME.LIGHT;
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setTheme] = useState<AppTheme>(THEME.LIGHT);
  const [isLoading, setIsLoading] = useState(false);

  const toggleTheme = async () => {
    const newTheme = theme === THEME.LIGHT ? THEME.DARK : THEME.LIGHT;
    const osTheme = getOSThemePreference();
    setIsLoading(true);

    try {
      await loadTheme(newTheme);
      setTheme(newTheme);

      if (newTheme !== osTheme) {
        localStorage.setItem('theme', newTheme);
      } else {
        localStorage.removeItem('theme');
      }

      document.body.className = `theme-${newTheme}`;
    } catch {
      // empty
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') as AppTheme;
    const osTheme = getOSThemePreference();

    const initialTheme = savedTheme || osTheme;

    loadTheme(initialTheme)
      .then(() => {
        setTheme(initialTheme);
        document.body.className = `theme-${initialTheme}`;
      })
      .catch(() => {
        setTheme(THEME.LIGHT);
      });
  }, []);

  return <ThemeContext.Provider value={{ theme, toggleTheme, isLoading }}>{children}</ThemeContext.Provider>;
};
